import { useState, useEffect, useCallback, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { Search, ShoppingBag, TrendingUp, Clock, Package, Loader2 } from "lucide-react";

interface OrderRow {
  readonly id: string;
  readonly orderNumber: number;
  readonly customerName: string;
  readonly customerPhone: string;
  readonly status: string;
  readonly total: number;
  readonly itemCount: number;
  readonly createdAt: string;
}

interface OrderStats {
  readonly totalOrders: number;
  readonly todayOrders: number;
  readonly todayRevenue: number;
  readonly pendingOrders: number;
}

interface OrderListResponse {
  readonly success: boolean;
  readonly data: readonly OrderRow[];
  readonly meta: { readonly total: number; readonly page: number; readonly limit: number };
}

const STATUS_MAP: Record<string, { label: string; bg: string; color: string; borderColor: string }> = {
  pending_payment: { label: "ממתין לתשלום", bg: "rgba(245,158,11,0.12)", color: "#B45309", borderColor: "#F59E0B" },
  paid:            { label: "שולם",          bg: "rgba(59,130,246,0.12)", color: "#1D4ED8", borderColor: "#3B82F6" },
  processing:      { label: "בטיפול",        bg: "rgba(124,58,237,0.12)", color: "#6D28D9", borderColor: "#7C3AED" },
  shipped:         { label: "נשלח",          bg: "rgba(6,182,212,0.12)",  color: "#0E7490", borderColor: "#06B6D4" },
  delivered:       { label: "נמסר",          bg: "rgba(16,185,129,0.12)", color: "#065F46", borderColor: "#10B981" },
  cancelled:       { label: "בוטל",          bg: "rgba(239,68,68,0.12)",  color: "#B91C1C", borderColor: "#EF4444" },
  refunded:        { label: "הוחזר",         bg: "rgba(249,115,22,0.12)", color: "#C2410C", borderColor: "#F97316" },
};

const STATUS_FILTER_OPTIONS = [
  { value: "", label: "הכל" },
  { value: "pending_payment", label: "ממתין" },
  { value: "processing", label: "בטיפול" },
  { value: "delivered", label: "הושלם" },
  { value: "cancelled", label: "בוטל" },
];

function formatNIS(agorot: number): string {
  return `₪${(agorot / 100).toFixed(2)}`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("he-IL", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function StatusPill({ status }: { readonly status: string }): ReactNode {
  const s = STATUS_MAP[status] ?? STATUS_MAP["pending_payment"]!;
  return (
    <span
      style={{
        background: s.bg,
        color: s.color,
        borderRadius: "20px",
        padding: "3px 10px",
        fontSize: "0.73rem",
        fontWeight: 700,
        display: "inline-flex",
        alignItems: "center",
        whiteSpace: "nowrap",
      }}
    >
      {s.label}
    </span>
  );
}

export function OrderListPage(): ReactNode {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [orders, setOrders] = useState<readonly OrderRow[]>([]);
  const [stats, setStats] = useState<OrderStats | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(true);
  const limit = 20;

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch("/api/orders/stats", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const json = (await res.json()) as { success: boolean; data: OrderStats };
      if (json.success) setStats(json.data);
    } catch { /* stats are non-critical */ }
  }, [accessToken]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (search) params.set("search", search);
      if (statusFilter) params.set("status", statusFilter);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);

      const res = await fetch(`/api/orders?${params}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const json = (await res.json()) as OrderListResponse;
      if (json.success) {
        setOrders(json.data);
        setTotal(json.meta.total);
      }
    } finally {
      setLoading(false);
    }
  }, [accessToken, page, search, statusFilter, dateFrom, dateTo]);

  useEffect(() => { void fetchStats(); }, [fetchStats]);
  useEffect(() => { void fetchOrders(); }, [fetchOrders]);

  const totalPages = Math.ceil(total / limit);

  return (
    <div style={{ padding: "1.5rem", display: "flex", flexDirection: "column", gap: "1.25rem" }}>

      {/* ── Header ── */}
      <div>
        <h1
          className="font-display text-gradient"
          style={{ fontWeight: 900, fontSize: "1.7rem", lineHeight: 1.2, margin: 0 }}
        >
          הזמנות
        </h1>
        {total > 0 && (
          <p style={{ fontSize: "0.85rem", color: "hsl(var(--muted-foreground))", marginTop: "0.2rem" }}>
            {total} הזמנות סה"כ
          </p>
        )}
      </div>

      {/* ── Stats cards ── */}
      {stats && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "0.75rem" }}
          className="md:grid-cols-4"
        >
          <StatCard
            icon={<ShoppingBag size={20} />}
            iconBg="rgba(59,130,246,0.12)"
            iconColor="#3B82F6"
            label='סה"כ הזמנות'
            value={String(stats.totalOrders)}
          />
          <StatCard
            icon={<Package size={20} />}
            iconBg="rgba(124,58,237,0.12)"
            iconColor="#7C3AED"
            label="היום"
            value={String(stats.todayOrders)}
          />
          <StatCard
            icon={<TrendingUp size={20} />}
            iconBg="rgba(16,185,129,0.12)"
            iconColor="#10B981"
            label="הכנסה היום"
            value={formatNIS(stats.todayRevenue)}
          />
          <StatCard
            icon={<Clock size={20} />}
            iconBg="rgba(245,158,11,0.12)"
            iconColor="#F59E0B"
            label="ממתינות"
            value={String(stats.pendingOrders)}
          />
        </div>
      )}

      {/* ── Search + Status filters + Date range ── */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.625rem", alignItems: "center" }}>
        {/* Search */}
        <div style={{ position: "relative", flex: "1 1 220px" }}>
          <Search
            size={16}
            style={{
              position: "absolute",
              insetInlineStart: "12px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "hsl(var(--muted-foreground))",
            }}
          />
          <input
            placeholder="חיפוש לפי מספר הזמנה, שם או טלפון..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{
              width: "100%",
              height: "42px",
              paddingInlineStart: "38px",
              paddingInlineEnd: "12px",
              borderRadius: "12px",
              border: "1.5px solid hsl(var(--border))",
              background: "hsl(var(--card))",
              color: "hsl(var(--foreground))",
              fontSize: "0.875rem",
              outline: "none",
              transition: "border-color 0.15s, box-shadow 0.15s",
              boxSizing: "border-box",
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = "var(--brand-primary)";
              e.currentTarget.style.boxShadow = "0 0 0 3px rgba(124,58,237,0.1)";
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = "hsl(var(--border))";
              e.currentTarget.style.boxShadow = "none";
            }}
          />
        </div>

        {/* Status filter pills */}
        <div style={{ display: "flex", gap: "0.375rem", flexWrap: "wrap" }}>
          {STATUS_FILTER_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => { setStatusFilter(opt.value); setPage(1); }}
              style={{
                height: "42px",
                padding: "0 1rem",
                borderRadius: "12px",
                border: "1.5px solid",
                borderColor: statusFilter === opt.value ? "var(--brand-primary)" : "hsl(var(--border))",
                background: statusFilter === opt.value ? "var(--brand-primary-light)" : "hsl(var(--card))",
                color: statusFilter === opt.value ? "var(--brand-primary)" : "hsl(var(--muted-foreground))",
                fontSize: "0.8rem",
                fontWeight: statusFilter === opt.value ? 700 : 500,
                cursor: "pointer",
                transition: "all 0.15s",
                whiteSpace: "nowrap",
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Date range */}
        <div style={{ display: "flex", gap: "0.375rem", flexWrap: "wrap" }}>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
            style={{
              height: "42px",
              padding: "0 0.75rem",
              borderRadius: "12px",
              border: "1.5px solid hsl(var(--border))",
              background: "hsl(var(--card))",
              color: "hsl(var(--foreground))",
              fontSize: "0.8rem",
              outline: "none",
              cursor: "pointer",
              minWidth: "130px",
              transition: "border-color 0.15s",
            }}
            onFocus={(e) => { e.currentTarget.style.borderColor = "var(--brand-primary)"; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = "hsl(var(--border))"; }}
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
            style={{
              height: "42px",
              padding: "0 0.75rem",
              borderRadius: "12px",
              border: "1.5px solid hsl(var(--border))",
              background: "hsl(var(--card))",
              color: "hsl(var(--foreground))",
              fontSize: "0.8rem",
              outline: "none",
              cursor: "pointer",
              minWidth: "130px",
              transition: "border-color 0.15s",
            }}
            onFocus={(e) => { e.currentTarget.style.borderColor = "var(--brand-primary)"; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = "hsl(var(--border))"; }}
          />
        </div>
      </div>

      {/* ── Orders content ── */}
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "4rem 0" }}>
          <Loader2
            className="animate-spin"
            size={32}
            style={{ color: "var(--brand-primary)" }}
          />
        </div>
      ) : orders.length === 0 ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "4rem 1rem",
            borderRadius: "var(--radius-lg)",
            border: "2px dashed hsl(var(--border))",
            background: "hsl(var(--card))",
            gap: "1rem",
            textAlign: "center",
          }}
        >
          <div
            style={{
              width: "64px", height: "64px", borderRadius: "50%",
              background: "var(--brand-primary-light)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <ShoppingBag size={28} style={{ color: "var(--brand-primary)" }} />
          </div>
          <div>
            <p style={{ fontWeight: 700, fontSize: "1rem", margin: 0 }}>אין הזמנות עדיין</p>
            <p style={{ fontSize: "0.875rem", color: "hsl(var(--muted-foreground))", marginTop: "0.25rem" }}>
              הזמנות יופיעו כאן לאחר שלקוחות יבצעו רכישות
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* ── Desktop table ── */}
          <div
            className="hidden md:block"
            style={{
              borderRadius: "var(--radius-lg)",
              border: "1.5px solid hsl(var(--border))",
              background: "hsl(var(--card))",
              boxShadow: "var(--shadow-card)",
              overflow: "hidden",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" }}>
              <thead>
                <tr style={{ borderBottom: "1.5px solid hsl(var(--border))", background: "hsl(var(--muted)/0.4)" }}>
                  {(["מספר הזמנה", "תאריך", "לקוח", "סטטוס"] as const).map((h) => (
                    <th
                      key={h}
                      style={{ padding: "0.75rem 1rem", textAlign: "start", fontSize: "0.75rem", fontWeight: 600, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.05em" }}
                    >
                      {h}
                    </th>
                  ))}
                  <th style={{ padding: "0.75rem 1rem", textAlign: "end", fontSize: "0.75rem", fontWeight: 600, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.05em" }}>סכום</th>
                  <th style={{ padding: "0.75rem 1rem", textAlign: "end", fontSize: "0.75rem", fontWeight: 600, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.05em" }}>פריטים</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o, i) => (
                  <OrderTableRow
                    key={o.id}
                    order={o}
                    isLast={i === orders.length - 1}
                    onNavigate={(id) => navigate(`/orders/${id}`)}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Mobile cards ── */}
          <div className="md:hidden" style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {orders.map((o) => (
              <OrderMobileCard
                key={o.id}
                order={o}
                onNavigate={(id) => navigate(`/orders/${id}`)}
              />
            ))}
          </div>

          {/* ── Pagination ── */}
          {totalPages > 1 && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem", paddingTop: "0.5rem" }}>
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                הקודם
              </Button>
              <span style={{ fontSize: "0.875rem", color: "hsl(var(--muted-foreground))", padding: "0 0.5rem" }}>
                {page} / {totalPages}
              </span>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                הבא
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ── Sub-components ─────────────────────────────────────────────────────────── */

interface StatCardProps {
  readonly icon: ReactNode;
  readonly iconBg: string;
  readonly iconColor: string;
  readonly label: string;
  readonly value: string;
}

function StatCard({ icon, iconBg, iconColor, label, value }: StatCardProps): ReactNode {
  return (
    <div
      style={{
        background: "hsl(var(--card))",
        borderRadius: "var(--radius-md)",
        border: "1.5px solid hsl(var(--border))",
        boxShadow: "var(--shadow-card)",
        padding: "1rem",
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
      }}
    >
      <div
        style={{
          width: "42px",
          height: "42px",
          borderRadius: "12px",
          background: iconBg,
          color: iconColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div>
        <p style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", margin: 0 }}>{label}</p>
        <p style={{ fontSize: "1.25rem", fontWeight: 800, margin: 0, lineHeight: 1.2 }}>{value}</p>
      </div>
    </div>
  );
}

interface OrderTableRowProps {
  readonly order: OrderRow;
  readonly isLast: boolean;
  readonly onNavigate: (id: string) => void;
}

function OrderTableRow({ order: o, isLast, onNavigate }: OrderTableRowProps): ReactNode {
  const [hovered, setHovered] = useState(false);

  return (
    <tr
      style={{
        borderBottom: isLast ? "none" : "1px solid hsl(var(--border))",
        background: hovered
          ? "linear-gradient(90deg, rgba(124,58,237,0.04) 0%, rgba(236,72,153,0.02) 100%)"
          : "transparent",
        cursor: "pointer",
        transition: "background 0.15s",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onNavigate(o.id)}
    >
      <td style={{ padding: "0.75rem 1rem", fontFamily: "monospace", fontWeight: 700, color: "var(--brand-primary)" }}>
        #{o.orderNumber}
      </td>
      <td style={{ padding: "0.75rem 1rem", color: "hsl(var(--muted-foreground))", fontSize: "0.8rem" }}>
        {formatDate(o.createdAt)}
      </td>
      <td style={{ padding: "0.75rem 1rem", fontWeight: 600 }}>{o.customerName}</td>
      <td style={{ padding: "0.75rem 1rem" }}>
        <StatusPill status={o.status} />
      </td>
      <td style={{ padding: "0.75rem 1rem", textAlign: "end", fontFamily: "monospace", fontWeight: 700 }}>
        {formatNIS(o.total)}
      </td>
      <td style={{ padding: "0.75rem 1rem", textAlign: "end", color: "hsl(var(--muted-foreground))" }}>
        {o.itemCount}
      </td>
    </tr>
  );
}

interface OrderMobileCardProps {
  readonly order: OrderRow;
  readonly onNavigate: (id: string) => void;
}

function OrderMobileCard({ order: o, onNavigate }: OrderMobileCardProps): ReactNode {
  const [hovered, setHovered] = useState(false);
  const statusInfo = STATUS_MAP[o.status] ?? STATUS_MAP["pending_payment"]!;

  return (
    <div
      style={{
        background: "hsl(var(--card))",
        borderRadius: "var(--radius-md)",
        border: "1.5px solid hsl(var(--border))",
        borderInlineStart: `4px solid ${statusInfo.borderColor}`,
        boxShadow: hovered ? "var(--shadow-card-hover)" : "var(--shadow-card)",
        transform: hovered ? "translateY(-2px)" : "none",
        transition: "all 0.2s ease",
        cursor: "pointer",
        padding: "0.75rem 1rem",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onNavigate(o.id)}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.375rem" }}>
        <span style={{ fontFamily: "monospace", fontWeight: 800, color: "var(--brand-primary)", fontSize: "0.9rem" }}>
          #{o.orderNumber}
        </span>
        <StatusPill status={o.status} />
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.25rem" }}>
        <span style={{ fontWeight: 600, fontSize: "0.875rem" }}>{o.customerName}</span>
        <span style={{ fontFamily: "monospace", fontWeight: 800, fontSize: "0.9rem" }}>{formatNIS(o.total)}</span>
      </div>
      <p style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", margin: 0 }}>
        {formatDate(o.createdAt)} &middot; {o.itemCount} פריטים
      </p>
    </div>
  );
}
